/**
 * Types and shared utilities for the Ability Grounds Lab panel.
 */
import {
  LOBBY_TOOL_DEFINITIONS,
  LOBBY_TOOL_MESSAGE,
  type LobbyToolId,
} from '../../../../shared/constants/LobbyTools';
import { ServiceRegistry } from '../../../core/ServiceRegistry';


export interface H2kProjectileRow {
  abilityId: string | null;
  width: number;
  height: number;
  shape: string | null;
  ownerId: string | null;
}

export interface H2kSnapshot {
  localSessionId: string | null;
  projectiles: H2kProjectileRow[];
  effects: Array<{ kind: string; abilityId?: string | null }>;
  castBars: Array<{ abilityId: string | null; progress: number }>;
  nameplates: Array<{ class: string | null; isLocal: boolean }>;
}

export type LabState = 'connecting' | 'preparing' | 'ready' | 'error';

export interface CaptureSweepState {
  running: boolean;
  completed: number;
  total: number;
  current: string | null;
  castIds: string[];
  error: string | null;
}

export interface AbilityOption {
  id: string;
  name: string;
  target?: string;
  cooldown?: number;
  description?: string;
}

export const CLASS_FALLBACK = 'mage';
export const SCENARIO_SPAWN_TOOLS = LOBBY_TOOL_DEFINITIONS.filter(
  (definition) => definition.kind === 'spawn_scenario_bot' && definition.groundsOnly,
);
export const GROUNDS_PREPARE_TIMEOUT_MS = 5_000;

export function readSnapshot(): H2kSnapshot | null {
  try {
    const fn = (window as any).__h2kRenderState;
    return typeof fn === 'function' ? (fn() as H2kSnapshot) : null;
  } catch {
    return null;
  }
}

export function selectGroundsTarget(targetId: string): boolean {
  try {
    const targeting = ServiceRegistry.getInstance().get('TargetingSystem', { optional: true });
    if (!targeting?.setTarget) return false;
    targeting.setTarget(targetId);
    return true;
  } catch {
    return false;
  }
}

export function roomOrNull(): any | null {
  try {
    return ServiceRegistry.getInstance().get('NetworkManager', { optional: true })?.getRoom?.() ?? null;
  } catch {
    return null;
  }
}


export function sendScenarioTool(tool: LobbyToolId, count?: number): boolean {
  const room = roomOrNull();
  if (!room) return false;
  room.send(LOBBY_TOOL_MESSAGE, count == null ? { tool } : { tool, count });
  return true;
}

export function statusTone(state: LabState): 'neutral' | 'warning' | 'success' | 'danger' {
  if (state === 'ready') return 'success';
  if (state === 'error') return 'danger';
  if (state === 'preparing') return 'warning';
  return 'neutral';
}
